import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './Style/Assessment.css';
import signout_n from './Image/signout_n.png';
import signout_d from './Image/signout_d.png';
import skip_n from './Image/skip_n.png';
import skip_d from './Image/skip_d.png';
import ok_n from './Image/ok_n.png';
import ok_d from './Image/ok_d.png';
import ok_blue_n from './Image/ok_blue_n.gif';
import ok_dblue_o from './Image/ok_dblue_o.gif';
import ok_dblue_d from './Image/ok_dblue_d.gif';
import exit_white_n from './Image/exit_white_n.gif';
import exit_white_o from './Image/exit_white_o.gif';
import exit_white_d from './Image/exit_white_d.gif';
import top_blue from './Image/top_blue_panel.png';
import Loading from '../../Common/Loading';
import BQns from '../../Common/Tutorials/BQns';
import OQns from '../../Common/Tutorials/OQns';
import RadioQns from '../../Common/Tutorials/RadioQns';
import ImageQns from '../../Common/Tutorials/ImageQns';
import CQns from '../../Common/Tutorials/CQns';
import MQns from '../../Common/Tutorials/MQns';
import WQns from '../../Common/Tutorials/WQns';
import IQns from '../../Common/Tutorials/IQns';
import FQns from '../../Common/Tutorials/FQns';
import LQns from '../../Common/Tutorials/LQns';
import DQns from '../../Common/Tutorials/DQns';
import NQns from '../../Common/Tutorials/NQns';
import { getQuestionTypeFromHtml } from '../../Common/Tutorials/utils/questionTypeUtils';


interface QuestionData {
  complete: boolean;
  progress: number;
  strand: number;
  sub_strand: number;
  year: number;
  topic: number;
  question_no: number;
  question_type: string;
  question_html: string;
  question_content: string;
  correct_answer: string | null;
  options: any[];
  blanks_count: number;
  blob_assets_path: string;
  lesson_code?: string;
}

interface AssessmentData {
  success: boolean;
  first_name: string;
  md_curriculum: string;
  study_year: number;
  lowest_year: number;
  highest_year: number;
  question_data: QuestionData;
}

interface SessionData {
  school_code: string;
  family_code: string;
  login: string;
  report_no: string;
}

const Assessment: React.FC = () => {
  const navigate = useNavigate();
  const [assessmentData, setAssessmentData] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [showTestComplete, setShowTestComplete] = useState(false);
  const [currentDate, setCurrentDate] = useState('');
  const questionRef = useRef<any>(null);
  const initializedRef = useRef(false);
  const submittingRef = useRef(false);

  // Prevent duplicate native alerts from child question components and
  // repeated React event handling, while still allowing different alerts.
  const lastAlertRef = useRef({ message: '', time: 0 });
  const alertUnlockTimerRef = useRef<number | null>(null);

  const [searchParams] = useSearchParams();

  const reportNo = searchParams.get('ReportNo') || '';
  const calledFrom = searchParams.get('CalledFrom') || '';

  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

  const sessionData: SessionData = {
    school_code: sessionStorage.getItem('SchoolCode') || '',
    family_code: sessionStorage.getItem('FamilyCode') || '',
    login: sessionStorage.getItem('Login') || '',
    report_no: reportNo,
  };

  // ---------------------------------------------------------------------------
  // MD ALERT PROTECTION
  // Child question components can raise the same alert more than once because
  // legacy question HTML/events may fire repeatedly. Keep one native alert
  // for the same message within a short window.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const nativeAlert = window.alert.bind(window);
    let lastNativeAlertMessage = '';
    let lastNativeAlertTime = 0;

    const protectedAlert = (message?: string) => {
      const normalizedMessage = String(message ?? '');
      const now = Date.now();

      if (
        normalizedMessage === lastNativeAlertMessage &&
        now - lastNativeAlertTime < 1500
      ) {
        return;
      }

      lastNativeAlertMessage = normalizedMessage;
      lastNativeAlertTime = now;
      nativeAlert(normalizedMessage);
    };

    window.alert = protectedAlert;

    return () => {
      window.alert = nativeAlert;
      if (alertUnlockTimerRef.current !== null) {
        window.clearTimeout(alertUnlockTimerRef.current);
        alertUnlockTimerRef.current = null;
      }
    };
  }, []);

  // ---------------------------------------------------------------------------
  // MD WHITE BACKGROUND
  // Keep the existing MD layout unchanged; only force the page/document
  // background to white.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const previousBodyBackground = document.body.style.backgroundColor;
    const previousHtmlBackground = document.documentElement.style.backgroundColor;
    const root = document.getElementById('root');
    const previousRootBackground = root?.style.backgroundColor ?? '';

    document.body.style.backgroundColor = '#fff';
    document.documentElement.style.backgroundColor = '#fff';

    if (root) {
      root.style.backgroundColor = '#fff';
    }

    return () => {
      document.body.style.backgroundColor = previousBodyBackground;
      document.documentElement.style.backgroundColor = previousHtmlBackground;

      if (root) {
        root.style.backgroundColor = previousRootBackground;
      }
    };
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    if (!sessionData.school_code || !sessionData.family_code || !sessionData.login) {
      alert('Session expired. Please login again.');
      window.location.href = '/login';
      return;
    }

    setCurrentDate(new Date().toLocaleDateString());
    initializeAssessment();
  }, []);


  const initializeAssessment = async () => {
    try {
      setShowMessage(true);
      setLoading(true);
      const response = await fetch(`${apiUrl}assessment/initialize/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`Server returned ${response.status}: ${text}`);
      }
      const data = await response.json();
      if (data.success) {
        setAssessmentData(data);
        if (data.question_data?.complete) {
          setShowTestComplete(true);
        }
      } else if (data.error) {
        alert(data.error);
      } else {
        alert('Unexpected response from server.');
      }
    } catch (error) {
      // console.error('Error initializing assessment:', error);
      alert('Error loading assessment. Please try again.');
    } finally {
      setShowMessage(false);
      setLoading(false);
    }
  };

  const checkLatestReport = async () => {
    setLoading(true);
    try {
      // console.log("Checking latest report...");
      const response = await fetch(`${apiUrl}get_latest_report_no/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          SchoolCode: sessionStorage.getItem("SchoolCode"),
          FamilyCode: sessionStorage.getItem("FamilyCode"),
          Login: sessionStorage.getItem("Login"),
        }),
      });
      // console.log("Response received from latest report check.");

      const data = await response.json();

      // console.log("Latest report data:", data);
      return data;
    } catch (err) {
      console.error("Error checking latest report:", err);
      return null;
    }
  };


  useEffect(() => {
    const handleNavigation = async () => {
      if (!showTestComplete) return;

      setLoading(true);
      try {
        // STEP 1: Get latest report info
        let latest = await checkLatestReport();
        // console.log("Initial latest report:", latest);

        // STEP 2: If student is on ReportNo=1 and StartWLP=0 → generate report
        if (
          latest &&
          latest.ReportNo === 1 &&
          latest.StartWLP === 0
        ) {
          // console.log("➡ Generating report because ReportNo=1 & StartWLP=0");

          await fetch(`${apiUrl}assessment/generate-report/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(sessionData),
          });

          // STEP 3: Fetch latest again AFTER report generation
          latest = await checkLatestReport();
          // console.log("Updated latest after report generation:", latest);
        }

        // STEP 4: Now check if everything is satisfied
        if (
          latest &&
          latest.ReportNo === 1 &&
          latest.StartWLP === 0 &&
          latest.MDStatus === "C"
        ) {
          // console.log("➡ All conditions satisfied → Navigating to StudyRoom");
          navigate("/STUDENT/MSR/StudyRoom?Subject=Mathematics&lastPage=Assessment");
          return;
        }

        // STEP 5: Otherwise go to MD Report & LP page
        // console.log("➡ Going to GenerateMDReportAndLP");

        const queryString = `?ReportNo=${encodeURIComponent(reportNo)}&CalledFrom=${encodeURIComponent(calledFrom)}`;
        navigate(`/MathsDoctor/Assessment/GenerateMDReportAndLP${queryString}`);

      } catch (err) {
        console.error("Navigation flow failed:", err);
      } finally {
        setLoading(false);
      }
    };

    handleNavigation();
  }, [showTestComplete]);


  const handleOkClick = async () => {
    if (submittingRef.current) return;
    if (!assessmentData || !questionRef.current) return;

    submittingRef.current = true;
    const answerData = questionRef.current.getAnswer();
    if (!answerData) {
      submittingRef.current = false;
      return;
    }

    setShowMessage(true);
    setLoading(true);
    try {
      const result = answerData.marks === 1 ? 'P' : 'F';
      const response = await fetch(`${apiUrl}assessment/submit-answer/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_code: sessionData.school_code,
          family_code: sessionData.family_code,
          login: sessionData.login,
          report_no: sessionData.report_no,
          strand: assessmentData.question_data.strand,
          sub_strand: assessmentData.question_data.sub_strand,
          year: assessmentData.question_data.year,
          topic: assessmentData.question_data.topic,
          question_no: assessmentData.question_data.question_no,
          result: result,
          correct_ans: answerData.enteredCorrectAnswer || '',
          wrong_ans: answerData.enteredWrongAnswer || '',
          blank_ans: answerData.enteredBlankAnswer || '',
          opt_order: answerData.optOrder || '',
          md_curriculum: assessmentData.md_curriculum,
          study_year: assessmentData.study_year,
          lowest_year: assessmentData.lowest_year,
          highest_year: assessmentData.highest_year,
        }),
      });
      const data = await response.json();
      if (data.success) {
        if (data.question_data.complete) {
          setShowTestComplete(true);
        } else {
          setAssessmentData(prev => prev ? { ...prev, question_data: data.question_data } : null);
        }
      } else {
        alert('Error submitting answer. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('Error submitting answer. Please try again.');
    } finally {
      submittingRef.current = false;
      setShowMessage(false);
      setLoading(false);
    }

  };

  const handleSkipClick = async () => {
    if (submittingRef.current) return;
    if (!assessmentData) return;

    submittingRef.current = true;
    setShowMessage(true);
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}assessment/submit-answer/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          school_code: sessionData.school_code,
          family_code: sessionData.family_code,
          login: sessionData.login,
          report_no: sessionData.report_no,
          strand: assessmentData.question_data.strand,
          sub_strand: assessmentData.question_data.sub_strand,
          year: assessmentData.question_data.year,
          topic: assessmentData.question_data.topic,
          question_no: assessmentData.question_data.question_no,
          result: 'S',
          correct_ans: '',
          wrong_ans: '',
          blank_ans: '',
          opt_order: '',
          md_curriculum: assessmentData.md_curriculum,
          study_year: assessmentData.study_year,
          lowest_year: assessmentData.lowest_year,
          highest_year: assessmentData.highest_year,
        }),
      });
      const data = await response.json();
      if (data.success) {
        if (data.question_data.complete) {
          setShowTestComplete(true);
        } else {
          setAssessmentData(prev => prev ? { ...prev, question_data: data.question_data } : null);
        }
      } else {
        alert('Error skipping question. Please try again.');
      }
    } catch (err) {
      console.error(err);
      alert('Error skipping question. Please try again.');
    } finally {
      submittingRef.current = false;
      setShowMessage(false);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    window.location.href = '/STUDENT/MSR/StudyRoom?Subject=Mathematics&lastPage=Assessment';
  };

  const handleSendEmail = async () => {
    try {
      await fetch(`${apiUrl}assessment/send-email/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData),
      });
      alert('Report sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email. Please try again.');
    }
  };

  const handleExit = () => {

    window.location.href = '/';
  };

  const showAlert = (message: string) => {
    const now = Date.now();

    if (
      lastAlertRef.current.message === message &&
      now - lastAlertRef.current.time < 1500
    ) {
      return;
    }

    lastAlertRef.current = {
      message,
      time: now,
    };

    if (alertUnlockTimerRef.current !== null) {
      window.clearTimeout(alertUnlockTimerRef.current);
    }

    alertUnlockTimerRef.current = window.setTimeout(() => {
      alertUnlockTimerRef.current = null;
    }, 1500);

    alert(message);
  };

  const renderQuestion = () => {
    if (!assessmentData || !assessmentData.question_data) return null;

    const {
      question_type,
      question_html,
      question_content
    } = assessmentData.question_data;

    const htmlToRender = question_html || question_content || '';

    let finalQnType = question_type;

    // IMPORTANT:
    // F type must always remain F.
    // O_IMAGE must always remain O_IMAGE.
    // Do not allow generic KSD_DIVIDERBAR detection to convert
    // either of these question types to O.
    if (question_type === 'F') {
      finalQnType = 'F';

    } else if (question_type === 'O_IMAGE') {
      // IMPORTANT:
      // O_IMAGE questions contain KSD_DIVIDERBAR in their HTML,
      // but they MUST use ImageQns.
      finalQnType = 'O_IMAGE';

    } else {

      // Check if KSD_DIVIDERBAR is present.
      let hasDividerBar = false;
      let hasNumpad = false;

      if (htmlToRender) {
        const normalizedHtml = htmlToRender.toLowerCase();

        // Check for KSD_DIVIDERBAR using multiple methods
        hasDividerBar =
          normalizedHtml.includes('ksd_dividerbar') ||
          !!htmlToRender.match(/id=["']KSD_DIVIDERBAR["']/i);

        // Check for KSD_NUMPAD
        hasNumpad =
          htmlToRender.includes('KSD_NUMPAD') ||
          !!htmlToRender.match(/id=["']KSD_NUMPAD["']/i);

        // Also try DOMParser for more accurate detection
        if (typeof DOMParser !== 'undefined') {
          try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              htmlToRender,
              'text/html'
            );

            if (
              doc.getElementById('KSD_DIVIDERBAR') ||
              doc.querySelector('[id*="KSD_DIVIDERBAR"]')
            ) {
              hasDividerBar = true;
            }

            if (doc.getElementById('KSD_NUMPAD')) {
              hasNumpad = true;
            }

          } catch (error) {
            console.error(
              'Error detecting question HTML structure:',
              error
            );
          }
        }
      }

      // Special case:
      // If question has KSD_NUMPAD and is type F/W/N,
      // keep the original question type.
      if (
        hasNumpad &&
        ['F', 'W', 'N'].includes(question_type)
      ) {
        finalQnType = question_type;

      }

      // If KSD_DIVIDERBAR is present, convert to O,
      // except types that have their own mechanics.
      else if (hasDividerBar) {

        // Preserve types that rely on their own
        // ordering / dragging / multi-select mechanics.
        if (
          question_type === 'C' ||
          question_type === 'L'
        ) {
          finalQnType = question_type;

        } else {
          finalQnType = 'O';
        }
      }

      // If question_type is already O,
      // use the same subtype detection logic as Question.tsx.
      else if (question_type === 'O') {

        const detectedType =
          getQuestionTypeFromHtml(
            htmlToRender,
            question_type
          );

        if (
          detectedType &&
          detectedType !== 'O'
        ) {
          finalQnType = detectedType;
        }
      }
    }

    const adjustLOptionsPadding = (html: string) => {
      if (
        !html ||
        typeof DOMParser === 'undefined'
      ) {
        return html;
      }

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(
          html,
          'text/html'
        );

        const optionCells =
          doc.querySelectorAll('td[name="0"]');

        optionCells.forEach((cell) => {
          cell.setAttribute('align', 'right');

          const existingStyle =
            cell.getAttribute('style') || '';

          const finalStyle =
            `${existingStyle}${existingStyle ? '; ' : ''}` +
            `padding-right: 20px !important; ` +
            `text-align: right !important;`;

          cell.setAttribute(
            'style',
            finalStyle
          );
        });

        return (
          doc.documentElement.outerHTML ||
          html
        );

      } catch {
        return html;
      }
    };

    const htmlForRender =
      finalQnType === 'L'
        ? adjustLOptionsPadding(htmlToRender)
        : htmlToRender;

    const commonProps = {
      ref: questionRef,
      questionHtml: htmlForRender,
      currentPage: 1,
      totalPages: 1,
      showAlert: showAlert,
      correctAudio: [],
      incorrectAudio: [],
      assetsBaseUrl:
        assessmentData.question_data.blob_assets_path,
      lessonMeta: null,
    };

    switch (finalQnType) {

      case 'B':
        return (
          <BQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'O':
        return (
          <OQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'O_RADIO':
        return (
          <OQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'O_IMAGE':
        return (
          <ImageQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'C':
        return (
          <CQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'M':
        return (
          <MQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'W':
        return (
          <WQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'I':
        return (
          <IQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'F':
        return (
          <FQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'L':
        return (
          <LQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'D':
        return (
          <DQns
            sessionId={null}
            {...commonProps}
          />
        );

      case 'N':
        return (
          <NQns
            sessionId={null}
            {...commonProps}
          />
        );

      default:
        return (
          <div
            style={{
              padding: '50px',
              textAlign: 'center'
            }}
          >
            <h3>
              Unsupported question type: {finalQnType}
            </h3>

            <p>
              Please contact support.
            </p>
          </div>
        );
    }
  };

  // Exact VB behaviour: backend returns 99 - completed percentage.
  const progressValue = assessmentData?.question_data?.progress ?? 99;
  const progressWidth = `${Math.max(0, Math.min(99, progressValue))}%`;

  return (
    <div
      id="bodyContent"
      className="assessment-md-page"
      style={{
        backgroundColor: '#fff',
        minHeight: '100vh',
      }}
    >
      <div id="Form">
        <div id="divContent">
          <div id="divAssessment" className="divAssessment">
            <div id="divMDButtons">
              <table cellPadding={0} border={0} cellSpacing={0} className="tableT">
                <tbody>
                  <tr
                    style={{
                      backgroundImage: `url(${top_blue})`,
                      backgroundRepeat: 'repeat-x',
                      height: '79px',
                    }}
                  >
                    <td className="TabledataT" style={{ paddingRight: "10px" }}>
                      <img
                        id="imgbtnLogout"
                        src={signout_n}
                        alt="Logout"
                        onMouseOver={(e) => (e.currentTarget.src = signout_d)}
                        onMouseOut={(e) => (e.currentTarget.src = signout_n)}
                        onClick={handleLogout}
                        style={{ cursor: 'pointer', paddingBottom: '9px', paddingLeft: '5px' }}
                      />
                    </td>
                    <td id="tdLoggedName" className="CssHead" style={{ paddingBottom: '13px', paddingRight: '20px' }}>
                      Logged in as {assessmentData?.first_name}
                    </td>
                    <td className="CssHeadT" style={{ paddingBottom: '13px', paddingRight: '10px', color: 'white', fontSize: '23px', paddingTop: '23px' }}>
                      Progress
                    </td>
                    <td className="Tabledata2" style={{ paddingBottom: '13px' }}>
                      <div
                        id="divProgressBarContainerT"
                        style={{
                          border: '2px solid silver',
                          backgroundColor: '#FFF',
                          // width: '400px',
                          // width:"22vw"
                        }}
                      >
                        <div
                          id="divProgressBar"
                          style={{
                            width: progressWidth,
                            height: '30px',
                            backgroundColor: 'rgb(19, 105, 172)',
                            boxSizing: 'border-box',

                          }}
                        ></div>
                      </div>
                    </td>
                    <td
                      className="Tabledata3"
                      style={{
                        paddingBottom: '9px',
                        textAlign: 'right',
                        paddingRight: '20px',
                      }}
                    >
                      <img
                        src={skip_n}
                        alt="Skip"
                        className="imgSkip"
                        onMouseOver={(e) => (e.currentTarget.src = skip_d)}
                        onMouseOut={(e) => (e.currentTarget.src = skip_n)}
                        onMouseDown={(e) => (e.currentTarget.src = skip_d)}
                        onClick={handleSkipClick}
                        style={{ cursor: 'pointer' }}
                      />
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <img
                        src={ok_n}
                        alt="OK"
                        className="imgOk"
                        onMouseOver={(e) => (e.currentTarget.src = ok_d)}
                        onMouseOut={(e) => (e.currentTarget.src = ok_n)}
                        onMouseDown={(e) => (e.currentTarget.src = ok_d)}
                        onClick={handleOkClick}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div id="divMDFrm" className="cssIfrm">
              <table style={{ width: '100%', height: '100%' }}>
                <tbody>
                  {renderQuestion()}
                </tbody>
              </table>
            </div>
          </div>
          <div
            id="divOpaque"
            className="divOpaque"
            style={{
              display: showMessage ? 'block' : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: '#cccccc',
              opacity: 0.8,
              zIndex: 200,
            }}
          ></div>
          <div
            id="divTestComplete"
            className="divTestComplete"
            style={{
              display: showTestComplete ? 'flex' : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'white',
              zIndex: 400,
              fontFamily: 'Arial',
              fontSize: '12pt',
              color: '#1760cf',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
          </div>
        </div>
      </div>
      {loading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}>
          <Loading />
        </div>
      )}
    </div>
  );
};

export default Assessment;
